import { createTheme, alpha } from '@mui/material/styles';
import { breakpoints, compactTypography, responsiveTypography } from './theme/responsive';
import { getDensityPreset, normalizeDensity, MOBILE_BREAKPOINT, MOBILE_DENSITY_MULTIPLIERS } from './theme/density';
import { applyGlobalStyles } from './styles/globalStyles';
import { tokens, MEDIA_QUERIES, CONTAINER_QUERIES } from './theme/tokens';
import { BASE_CSS_VARIABLES } from './theme/cssVariables';

// Base theme configuration without mode-specific colors
const baseThemeConfig = {
  breakpoints,
  typography: {
    fontFamily:
      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
    // Standardize weights across the app
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
  },
  shape: {
    // MUI's `sx={{ borderRadius: n }}` multiplies by this base value. Keep this
    // aligned with the "2-4 => 8-16px" design guidance across the app.
    borderRadius: 4,
  },
};

// Light mode palette
const lightPalette = {
  mode: 'light',
  primary: {
    main: '#0066FF', // Vibrant cobalt blue
    light: '#69B3FF',
    dark: '#0052CC',
    contrastText: '#fff',
  },
  secondary: {
    main: '#00B8E6', // Bright cyan
    light: '#40D9FF',
    dark: '#0095CC',
    contrastText: '#fff',
  },
  background: {
    default: '#F8FAFF', // Light blue-tinted background
    paper: '#fff',
  },
  text: {
    primary: '#0F172A', // Modern dark text
    secondary: '#475569', // Modern muted text
  },
  divider: 'rgba(0, 0, 0, 0.06)',
  error: {
    main: '#EF4444',
    light: '#FCA5A5',
    dark: '#DC2626',
  },
  warning: {
    main: '#F59E0B',
    light: '#FCD34D',
    dark: '#D97706',
  },
  success: {
    main: '#10B981',
    light: '#6EE7B7',
    dark: '#059669',
  },
  info: {
    main: '#0EA5E9',
    light: '#7DD3FC',
    dark: '#0284C7',
  },
};

// Dark mode palette
const darkPalette = {
  mode: 'dark',
  primary: {
    main: '#4D9FFF', // Lighter blue for dark mode
    light: '#80BFFF',
    dark: '#0066FF',
    contrastText: '#fff',
  },
  secondary: {
    main: '#40D9FF', // Lighter cyan for dark mode
    light: '#73E6FF',
    dark: '#00B8E6',
    contrastText: '#000',
  },
  background: {
    default: '#0F172A', // Dark navy background
    paper: '#1E293B', // Slightly lighter dark for cards
  },
  text: {
    primary: '#F1F5F9', // Light text
    secondary: '#CBD5E1', // Muted light text
  },
  divider: 'rgba(255, 255, 255, 0.08)',
  error: {
    main: '#F87171',
    light: '#FCA5A5',
    dark: '#EF4444',
  },
  warning: {
    main: '#FBBF24',
    light: '#FCD34D',
    dark: '#F59E0B',
  },
  success: {
    main: '#34D399',
    light: '#6EE7B7',
    dark: '#10B981',
  },
  info: {
    main: '#38BDF8',
    light: '#7DD3FC',
    dark: '#0EA5E9',
  },
};

// Legacy compact-mode overrides (kept for reference)
const getLegacyCompactComponents = () => ({
  MuiButton: {
    defaultProps: { size: 'small' },
    styleOverrides: {
      root: { textTransform: 'none' },
      sizeSmall: { padding: '3px 10px', minHeight: 26 },
      sizeMedium: { padding: '5px 14px', minHeight: 32 },
    },
  },
  MuiTextField: {
    defaultProps: { size: 'small' },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: { padding: '6px 10px' },
      inputSizeSmall: { padding: '4px 8px' },
    },
  },
  MuiTable: {
    defaultProps: { size: 'small' },
  },
  MuiTableCell: {
    styleOverrides: {
      root: { padding: '6px 12px', fontSize: '0.75rem' },
      sizeSmall: { padding: '4px 8px' },
      head: { fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: { padding: 12, '&:last-child': { paddingBottom: 12 } },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: { padding: '12px 16px', fontSize: '1rem' },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: { padding: '8px 16px' },
    },
  },
  MuiChip: {
    defaultProps: { size: 'small' },
    styleOverrides: {
      root: { height: 22, fontSize: '0.6875rem' },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: { minHeight: 36, padding: '6px 12px', fontSize: '0.8125rem', textTransform: 'none' },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: { minHeight: 36 },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: { padding: '4px 12px', fontSize: '0.75rem' },
    },
  },
});

// Main theme creation function with compact mode support
export const createAppThemeLegacy = (mode = 'light', compactMode = false) => {
  const palette = mode === 'dark' ? darkPalette : lightPalette;
  const typography = compactMode ? compactTypography : responsiveTypography;    
  const spacing = compactMode ? 4 : 8;
  const compactComponents = compactMode ? getLegacyCompactComponents() : {};

  const base = createTheme({
    ...baseThemeConfig,
    palette,
    typography,
    spacing,
    components: {
      ...compactComponents,
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 44,
            padding: '4px',
            borderRadius: 12,
            backgroundColor: alpha(palette.primary.main, palette.mode === 'light' ? 0.04 : 0.08),
            backdropFilter: 'saturate(140%) blur(2px)'
          },
          indicator: {
            display: 'none',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            minHeight: 36,
            padding: '6px 12px',
            marginRight: 6,
            borderRadius: 999,
            color: palette.text.secondary,
            '&.Mui-selected': {
              backgroundColor: alpha(palette.primary.main, palette.mode === 'light' ? 0.12 : 0.24),
              color: palette.mode === 'light' ? palette.primary.dark : palette.primary.light,
              fontWeight: baseThemeConfig.typography.fontWeightBold,
            },
            '&:hover': {
              backgroundColor: alpha(palette.primary.main, palette.mode === 'light' ? 0.08 : 0.16),
            },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            // Drive rems from the app-level CSS variable (controlled by FontSizeProvider)
            fontSize: 'var(--app-font-size, 16px)',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            // Expose common typography tokens as CSS variables for consistency in sx/style
            '--vp-font-weight-regular': 400,
            '--vp-font-weight-medium': 500,
            '--vp-font-weight-bold': 700,
            '--vp-line-height-body': 1.6,
          },
          body: {
            fontFeatureSettings: '"kern" 1, "liga" 1',
            textRendering: 'optimizeLegibility',
          },
        },
      },
      // Additional non-compact components here
    },
  });

  // Apply global style enhancements consistently
  return applyGlobalStyles(base);
};

const mergeAtMedia = (style, query, patch) => {
  if (!patch || Object.keys(patch).length === 0) return;
  style[query] = { ...(style[query] || {}), ...patch };
};

const buildResponsiveTypographyVariant = ({ fontSize, lineHeight, ...rest }) => {
  const variant = { ...rest };

  const fontSizeDefault =
    typeof fontSize === 'string' ? fontSize : fontSize?.md || fontSize?.sm || fontSize?.xs;
  if (fontSizeDefault) variant.fontSize = fontSizeDefault;

  if (fontSize && typeof fontSize === 'object') {
    mergeAtMedia(variant, MEDIA_QUERIES.xs, fontSize.xs ? { fontSize: fontSize.xs } : null);
    mergeAtMedia(variant, MEDIA_QUERIES.sm, fontSize.sm ? { fontSize: fontSize.sm } : null);
    mergeAtMedia(variant, MEDIA_QUERIES.lg, fontSize.lg ? { fontSize: fontSize.lg } : null);
    mergeAtMedia(variant, MEDIA_QUERIES.xl, fontSize.xl ? { fontSize: fontSize.xl } : null);
  }

  const lineHeightDefault =
    typeof lineHeight === 'number'
      ? lineHeight
      : lineHeight?.md || lineHeight?.sm || lineHeight?.xs || lineHeight;
  if (lineHeightDefault) variant.lineHeight = lineHeightDefault;

  if (lineHeight && typeof lineHeight === 'object') {
    mergeAtMedia(variant, MEDIA_QUERIES.xs, lineHeight.xs ? { lineHeight: lineHeight.xs } : null);
    mergeAtMedia(variant, MEDIA_QUERIES.sm, lineHeight.sm ? { lineHeight: lineHeight.sm } : null);
    mergeAtMedia(variant, MEDIA_QUERIES.lg, lineHeight.lg ? { lineHeight: lineHeight.lg } : null);
    mergeAtMedia(variant, MEDIA_QUERIES.xl, lineHeight.xl ? { lineHeight: lineHeight.xl } : null);
  }

  return variant;
};

const buildTypographyFromScale = (typographyScale) => {
  const typography = { ...baseThemeConfig.typography };
  const variants = [
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'subtitle1',
    'subtitle2',
    'body1',
    'body2',
    'caption',
    'button',
    'overline',
  ];

  variants.forEach((variant) => {
    if (!typographyScale?.[variant]) return;
    typography[variant] = buildResponsiveTypographyVariant(typographyScale[variant]);
  });

  return typography;
};

const getCoarsePointerMinHeight = (densityId) => (densityId === 'comfortable' ? 48 : 44);

const getDensityComponents = (palette, densityPreset) => {
  const coarseMinHeight = getCoarsePointerMinHeight(densityPreset.id);
  const iconCoarseSize = 44;

  const buttonSizes = densityPreset.components.button;
  const iconButtonSizes = densityPreset.components.iconButton;
  const chipSizes = densityPreset.components.chip;
  const tabsSizes = densityPreset.components.tabs;
  const tableCell = densityPreset.components.tableCell;
  const cardContent = densityPreset.components.cardContent;
  const dialog = densityPreset.components.dialog;
  const input = densityPreset.components.input;
  const dataGrid = densityPreset.components.dataGrid;
  const mobile = densityPreset.mobile;

  const buttonCoarsePadding = densityPreset.id === 'comfortable' ? '10px 16px' : '8px 16px';

  return {
    MuiButton: {
      defaultProps: { size: buttonSizes.defaultSize },
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          transition: 'all 0.2s',
          '&:focus-visible': {
            outline: `3px solid ${
              palette.mode === 'light' ? palette.primary.dark : palette.primary.main
            }`,
            outlineOffset: '2px',
            boxShadow: `0 0 0 4px ${alpha(palette.primary.main, 0.25)}`,
          },
          '&:hover': {
            '@media (hover: hover)': {
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 12px ${alpha(palette.primary.main, 0.15)}`,
            },
          },
        },
        sizeSmall: {
          ...buttonSizes.sizeSmall,
          '@media (pointer: coarse)': {
            minHeight: coarseMinHeight,
            padding: buttonCoarsePadding,
          },
        },
        sizeMedium: {
          ...buttonSizes.sizeMedium,
          '@media (pointer: coarse)': {
            minHeight: Math.max(coarseMinHeight, 48),
            padding: buttonCoarsePadding,
          },
        },
        sizeLarge: {
          ...buttonSizes.sizeLarge,
          '@media (pointer: coarse)': {
            minHeight: Math.max(coarseMinHeight, 52),
            padding: buttonCoarsePadding,
          },
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${palette.primary.main} 0%, ${palette.primary.dark} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${palette.primary.dark} 0%, ${palette.primary.main} 100%)`,
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
          },
        },
      },
    },
    MuiIconButton: {
      defaultProps: { size: iconButtonSizes.defaultSize },
      styleOverrides: {
        root: {
          aspectRatio: '1/1',
          borderRadius: '50%',
          transition: 'all 0.2s',
          '&:focus-visible': {
            outline: `3px solid ${
              palette.mode === 'light' ? palette.primary.dark : palette.primary.main
            }`,
            outlineOffset: '2px',
            boxShadow: `0 0 0 4px ${alpha(palette.primary.main, 0.25)}`,
          },
          '&:hover': {
            transform: 'scale(1.05)',
          },
        },
        sizeSmall: {
          width: iconButtonSizes.sizeSmall.width,
          height: iconButtonSizes.sizeSmall.height,
          '@media (pointer: coarse)': {
            width: Math.max(iconCoarseSize, iconButtonSizes.sizeSmall.width),
            height: Math.max(iconCoarseSize, iconButtonSizes.sizeSmall.height),
          },
        },
        sizeMedium: {
          width: iconButtonSizes.sizeMedium.width,
          height: iconButtonSizes.sizeMedium.height,
          '@media (pointer: coarse)': {
            width: Math.max(iconCoarseSize, iconButtonSizes.sizeMedium.width),
            height: Math.max(iconCoarseSize, iconButtonSizes.sizeMedium.height),
          },
        },
        sizeLarge: {
          width: iconButtonSizes.sizeLarge.width,
          height: iconButtonSizes.sizeLarge.height,
          '@media (pointer: coarse)': {
            width: Math.max(iconCoarseSize, iconButtonSizes.sizeLarge.width),
            height: Math.max(iconCoarseSize, iconButtonSizes.sizeLarge.height),
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.xl,
          boxShadow: `0 1px 3px ${
            palette.mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(0, 0, 0, 0.12)'
          }`,
          transition: `all ${tokens.transitions.duration.slow} ${tokens.transitions.easing.easeInOut}`,
          backgroundImage: 'none',
          containerType: 'inline-size',
          '&:hover': {
            boxShadow: `0 4px 20px ${
              palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(0, 0, 0, 0.18)'
            }`,
          },
          [MOBILE_BREAKPOINT]: {
            borderRadius: mobile.card.borderRadius,
          },
          [CONTAINER_QUERIES.sm]: {
            '& .MuiCardContent-root': {
              paddingInline: tokens.spacing.sm,
              paddingBlock: tokens.spacing.sm,
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: tokens.radius.lg,
          [MOBILE_BREAKPOINT]: {
            borderRadius: mobile.paper.borderRadius,
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: input.defaultSize,
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            transition: 'all 0.2s',
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: palette.primary.main,
              },
            },
            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: palette.primary.main,
                borderWidth: 2,
              },
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: { padding: input.inputPadding },
        inputSizeSmall: { padding: input.inputPaddingSmall },
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: 'outlined',
        size: input.defaultSize,
      },
    },
    MuiChip: {
      defaultProps: { size: chipSizes.defaultSize },
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
        sizeSmall: {
          height: chipSizes.sizeSmall.height,
          fontSize: chipSizes.sizeSmall.fontSize,
        },
        sizeMedium: {
          height: chipSizes.sizeMedium.height,
          fontSize: chipSizes.sizeMedium.fontSize,
        },
        filled: {
          backgroundColor: alpha(palette.primary.main, palette.mode === 'light' ? 0.08 : 0.16),
          color: palette.primary.main,
          '&.MuiChip-colorSuccess': {
            backgroundColor: alpha(palette.success.main, palette.mode === 'light' ? 0.08 : 0.16),
            color: palette.success.main,
          },
          '&.MuiChip-colorError': {
            backgroundColor: alpha(palette.error.main, palette.mode === 'light' ? 0.08 : 0.16),
            color: palette.error.main,
          },
          '&.MuiChip-colorWarning': {
            backgroundColor: alpha(palette.warning.main, palette.mode === 'light' ? 0.08 : 0.16),
            color: palette.warning.main,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding:
            densityPreset.id === 'emr'
              ? '4px 10px'
              : densityPreset.id === 'compact'
                ? '6px 12px'
                : '8px 14px',
          fontSize:
            densityPreset.id === 'emr'
              ? '0.75rem'
              : densityPreset.id === 'compact'
                ? '0.8125rem'
                : '0.875rem',
          [MOBILE_BREAKPOINT]: {
            borderRadius: mobile.paper.borderRadius,
            padding:
              densityPreset.id === 'emr'
                ? '3px 8px'
                : densityPreset.id === 'compact'
                  ? '4px 10px'
                  : '6px 12px',
          },
        },
        standardSuccess: {
          backgroundColor: alpha(palette.success.main, palette.mode === 'light' ? 0.08 : 0.16),
          color: palette.mode === 'light' ? palette.success.dark : palette.success.light,
        },
        standardError: {
          backgroundColor: alpha(palette.error.main, palette.mode === 'light' ? 0.08 : 0.16),
          color: palette.mode === 'light' ? palette.error.dark : palette.error.light,
        },
        standardWarning: {
          backgroundColor: alpha(palette.warning.main, palette.mode === 'light' ? 0.08 : 0.16),
          color: palette.mode === 'light' ? palette.warning.dark : palette.warning.light,
        },
        standardInfo: {
          backgroundColor: alpha(palette.info.main, palette.mode === 'light' ? 0.08 : 0.16),
          color: palette.mode === 'light' ? palette.info.dark : palette.info.light,
        },
      },
    },
    MuiTable: {
      defaultProps: { size: densityPreset.id === 'comfortable' ? 'medium' : 'small' },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: tableCell.padding,
          fontSize: tableCell.fontSize,
          borderColor: palette.divider,
          [MOBILE_BREAKPOINT]: {
            padding: mobile.tableCell.padding,
          },
        },
        head: {
          fontWeight: 600,
          fontSize: tableCell.headFontSize,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          backgroundColor: palette.background.default,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          [MOBILE_BREAKPOINT]: {
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          paddingInline: cardContent.padding,
          paddingBlock: cardContent.padding,
          '&:last-child': { paddingBlockEnd: cardContent.padding },
          [MOBILE_BREAKPOINT]: {
            paddingInline: mobile.cardContent.padding,
            paddingBlock: mobile.cardContent.padding,
            '&:last-child': { paddingBlockEnd: mobile.cardContent.padding },
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: dialog.titlePadding,
          fontSize: dialog.titleFontSize,
          [MOBILE_BREAKPOINT]: {
            padding: mobile.dialog.titlePadding,
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: dialog.contentPadding,
          [MOBILE_BREAKPOINT]: {
            padding: mobile.dialog.contentPadding,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        container: {
          [MOBILE_BREAKPOINT]: {
            alignItems: 'stretch',
          },
        },
        paper: {
          borderRadius: 16,
          boxShadow: `0 20px 40px ${alpha('#000', palette.mode === 'light' ? 0.1 : 0.3)}`,
          [MOBILE_BREAKPOINT]: {
            borderRadius: 0,
            margin: 0,
            width: '100%',
            maxWidth: '100%',
            height: '100%',
            maxHeight: '100%',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: `0 1px 3px ${
            palette.mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(0, 0, 0, 0.12)'
          }`,
          backgroundColor: palette.background.paper,
          color: palette.text.primary,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: `1px solid ${palette.divider}`,
          backgroundColor: palette.background.paper,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.md,
          marginBlock: densityPreset.id === 'emr' ? 1 : 2,
          marginInline: densityPreset.id === 'emr' ? 6 : 8,
          '&.Mui-selected': {
            backgroundColor: alpha(palette.primary.main, palette.mode === 'light' ? 0.08 : 0.16),
            color: palette.primary.main,
            '&:hover': {
              backgroundColor: alpha(palette.primary.main, palette.mode === 'light' ? 0.12 : 0.24),
            },
          },
          [MOBILE_BREAKPOINT]: {
            marginBlock: 1,
            marginInline: 4,
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          '&.Mui-selected': {
            backgroundColor: palette.primary.main,
            color: palette.primary.contrastText,
            '&:hover': {
              backgroundColor: palette.primary.dark,
            },
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: alpha(palette.primary.main, palette.mode === 'light' ? 0.08 : 0.16),
        },
        bar: {
          borderRadius: 4,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: tabsSizes.tabsMinHeight,
          padding: '4px',
          borderRadius: 12,
          backgroundColor: alpha(palette.primary.main, palette.mode === 'light' ? 0.04 : 0.08),
          backdropFilter: 'saturate(140%) blur(2px)',
          [MOBILE_BREAKPOINT]: {
            borderRadius: mobile.paper.borderRadius,
            padding: '3px',
          },
        },
        scroller: {
          [MOBILE_BREAKPOINT]: {
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          },
        },
        flexContainer: {
          [MOBILE_BREAKPOINT]: {
            flexWrap: 'nowrap',
          },
        },
        indicator: {
          display: 'none',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          minHeight: tabsSizes.tabMinHeight,
          padding: tabsSizes.tabPadding,
          marginRight: 6,
          borderRadius: 999,
          color: palette.text.secondary,
          '&.Mui-selected': {
            backgroundColor: alpha(palette.primary.main, palette.mode === 'light' ? 0.12 : 0.24),
            color: palette.mode === 'light' ? palette.primary.dark : palette.primary.light,
            fontWeight: baseThemeConfig.typography.fontWeightBold,
          },
          '&:hover': {
            backgroundColor: alpha(palette.primary.main, palette.mode === 'light' ? 0.08 : 0.16),
          },
          [MOBILE_BREAKPOINT]: {
            minHeight: mobile.tabs.tabMinHeight,
            padding: mobile.tabs.tabPadding,
            marginRight: 4,
          },
        },
      },
    },
    MuiDataGrid: {
      defaultProps: {
        density: dataGrid.density,
        rowHeight: dataGrid.rowHeight,
        columnHeaderHeight: dataGrid.columnHeaderHeight,
      },
      styleOverrides: {
        root: {
          fontSize: tableCell.fontSize,
        },
      },
    },
  };
};

// Main theme creation function with density support.
// Backward compatible: second arg may be a boolean `compactMode`.
export const createAppTheme = (mode = 'light', densityOrCompact = undefined) => {
  const palette = mode === 'dark' ? darkPalette : lightPalette;
  const density =
    typeof densityOrCompact === 'boolean'
      ? densityOrCompact
        ? 'compact'
        : 'comfortable'
      : normalizeDensity(densityOrCompact);

  const preset = getDensityPreset(density);
  const typography = buildTypographyFromScale(preset.typographyScale);

  const base = createTheme({
    ...baseThemeConfig,
    palette,
    typography,
    spacing: preset.spacing,
    virtualpat: {
      density: preset.id,
      densityPreset: preset,
    },
    components: {
      ...getDensityComponents(palette, preset),
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            ...BASE_CSS_VARIABLES,
          },
          html: {
            fontSize: 'var(--app-font-size, 16px)',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            '--vp-density': preset.id,
            '--vp-density-spacing': `${preset.spacing}px`,
            '--vp-control-height-sm': `${preset.components.button.sizeSmall.minHeight}px`,
            '--vp-control-height-md': `${preset.components.button.sizeMedium.minHeight}px`,
            '--vp-control-height-lg': `${preset.components.button.sizeLarge.minHeight}px`,
            '--vp-card-padding': `${preset.components.cardContent.padding}px`,
            '--vp-card-border-radius': `${tokens.radius.xl}px`,
            '--vp-paper-border-radius': `${tokens.radius.lg}px`,
            '--vp-dialog-border-radius': `${tokens.radius.xl}px`,
            '--vp-focus-ring-color': palette.mode === 'light' ? palette.primary.dark : palette.primary.main,
            '--vp-focus-ring-width': '3px',
            '--vp-focus-ring-offset': '2px',
            [MOBILE_BREAKPOINT]: {
              '--vp-density-spacing': `${Math.round(preset.spacing * MOBILE_DENSITY_MULTIPLIERS.spacing)}px`,
              '--vp-card-padding': `${preset.mobile.cardContent.padding}px`,
              '--vp-card-border-radius': `${preset.mobile.card.borderRadius}px`,
              '--vp-paper-border-radius': `${preset.mobile.paper.borderRadius}px`,
              '--vp-dialog-border-radius': `${preset.mobile.dialog.borderRadius}px`,
            },
          },
          body: {
            fontFeatureSettings: '"kern" 1, "liga" 1',
            textRendering: 'optimizeLegibility',
          },
          'button:focus-visible, [role="button"]:focus-visible, a:focus-visible, .MuiButton-root:focus-visible, .MuiIconButton-root:focus-visible, .MuiCardActionArea-root:focus-visible':
            {
              outline: 'var(--vp-focus-ring-width) solid var(--vp-focus-ring-color)',
              outlineOffset: 'var(--vp-focus-ring-offset)',
              boxShadow: `0 0 0 4px ${alpha(palette.primary.main, 0.25)}`,
            },
          [MEDIA_QUERIES.reducedMotion]: {
            '*': {
              animation: 'none !important',
              transition: 'none !important',
              scrollBehavior: 'auto !important',
            },
          },
          [MEDIA_QUERIES.highContrast]: {
            ':root': {
              '--vp-focus-ring-width': '4px',
              '--vp-border-width': '2px',
            },
            '*': {
              borderWidth: 'var(--vp-border-width, 1px)',
            },
          },
          [MEDIA_QUERIES.forcedColors]: {
            'button:focus-visible, [role="button"]:focus-visible, a:focus-visible': {
              outline: '3px solid CanvasText',
              outlineOffset: '2px',
            },
          },
          '.sr-only': {
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          },
          '.skip-link': {
            position: 'absolute',
            insetBlockStart: '-100px',
            insetInlineStart: 0,
            backgroundColor: palette.background.paper,
            color: palette.primary.main,
            padding: tokens.spacing.sm,
            zIndex: tokens.zIndex.tooltip,
            textDecoration: 'none',
            fontWeight: tokens.typography.fontWeight.semibold,
            '&:focus': {
              insetBlockStart: 0,
            },
          },
        },
      },
    },
  });

  return applyGlobalStyles(base);
};

// Default theme (legacy compatibility)
const theme = createTheme({
  ...baseThemeConfig,
  palette: lightPalette,
  typography: {
    fontFamily:
      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
    h1: {
      fontWeight: 700,
      fontSize: responsiveTypography.h1.fontSize.md,
      lineHeight: responsiveTypography.h1.lineHeight.md,
      letterSpacing: '-0.01562em',
      '@media (max-width: 600px)': {
        fontSize: responsiveTypography.h1.fontSize.xs,
        lineHeight: responsiveTypography.h1.lineHeight.xs,
      },
      '@media (min-width: 600px) and (max-width: 900px)': {
        fontSize: responsiveTypography.h1.fontSize.sm,
        lineHeight: responsiveTypography.h1.lineHeight.sm,
      },
      '@media (min-width: 1200px)': {
        fontSize: responsiveTypography.h1.fontSize.lg,
      },
    },
    h2: {
      fontWeight: 700,
      fontSize: responsiveTypography.h2.fontSize.md,
      lineHeight: responsiveTypography.h2.lineHeight,
      letterSpacing: '-0.00833em',
      '@media (max-width: 600px)': {
        fontSize: responsiveTypography.h2.fontSize.xs,
      },
      '@media (min-width: 600px) and (max-width: 900px)': {
        fontSize: responsiveTypography.h2.fontSize.sm,
      },
      '@media (min-width: 1200px)': {
        fontSize: responsiveTypography.h2.fontSize.lg,
      },
    },
    h3: {
      fontWeight: 600,
      fontSize: responsiveTypography.h3.fontSize.md,
      lineHeight: responsiveTypography.h3.lineHeight,
      letterSpacing: '0em',
      '@media (max-width: 600px)': {
        fontSize: responsiveTypography.h3.fontSize.xs,
      },
      '@media (min-width: 600px) and (max-width: 900px)': {
        fontSize: responsiveTypography.h3.fontSize.sm,
      },
      '@media (min-width: 1200px)': {
        fontSize: responsiveTypography.h3.fontSize.lg,
      },
    },
    h4: {
      fontWeight: 600,
      fontSize: responsiveTypography.h4.fontSize.md,
      lineHeight: responsiveTypography.h4.lineHeight,
      letterSpacing: '0.00735em',
      '@media (max-width: 600px)': {
        fontSize: responsiveTypography.h4.fontSize.xs,
      },
      '@media (min-width: 600px) and (max-width: 900px)': {
        fontSize: responsiveTypography.h4.fontSize.sm,
      },
    },
    h5: {
      fontWeight: 600,
      fontSize: responsiveTypography.h5.fontSize.md,
      lineHeight: responsiveTypography.h5.lineHeight,
      letterSpacing: '0em',
      '@media (max-width: 600px)': {
        fontSize: responsiveTypography.h5.fontSize.xs,
      },
      '@media (min-width: 600px) and (max-width: 900px)': {
        fontSize: responsiveTypography.h5.fontSize.sm,
      },
    },
    h6: {
      fontWeight: 600,
      fontSize: responsiveTypography.h6.fontSize.md,
      lineHeight: responsiveTypography.h6.lineHeight,
      letterSpacing: '0.0075em',
      '@media (max-width: 600px)': {
        fontSize: responsiveTypography.h6.fontSize.xs,
      },
      '@media (min-width: 600px) and (max-width: 900px)': {
        fontSize: responsiveTypography.h6.fontSize.sm,
      },
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
      '@media (max-width: 600px)': {
        fontSize: '0.9375rem',
      },
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.5,
      '@media (max-width: 600px)': {
        fontSize: '0.8125rem',
      },
    },
    body1: {
      fontSize: responsiveTypography.body1.fontSize.md,
      lineHeight: responsiveTypography.body1.lineHeight,
      '@media (max-width: 600px)': {
        fontSize: responsiveTypography.body1.fontSize.xs,
      },
      '@media (min-width: 600px) and (max-width: 900px)': {
        fontSize: responsiveTypography.body1.fontSize.sm,
      },
    },
    body2: {
      fontSize: responsiveTypography.body2.fontSize.md,
      lineHeight: responsiveTypography.body2.lineHeight,
      '@media (max-width: 600px)': {
        fontSize: responsiveTypography.body2.fontSize.xs,
      },
      '@media (min-width: 600px) and (max-width: 900px)': {
        fontSize: responsiveTypography.body2.fontSize.sm,
      },
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      '@media (max-width: 600px)': {
        fontSize: '0.875rem',
      },
    },
    caption: {
      fontSize: responsiveTypography.caption.fontSize.md,
      lineHeight: responsiveTypography.caption.lineHeight,
      '@media (max-width: 600px)': {
        fontSize: responsiveTypography.caption.fontSize.xs,
      },
      '@media (min-width: 600px) and (max-width: 900px)': {
        fontSize: responsiveTypography.caption.fontSize.sm,
      },
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.08333em',
      textTransform: 'uppercase',
      '@media (max-width: 600px)': {
        fontSize: '0.6875rem',
      },
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(0, 0, 0, 0.04)',
    '0px 1px 3px rgba(0, 0, 0, 0.04)',
    '0px 2px 4px rgba(0, 0, 0, 0.04)',
    '0px 3px 5px rgba(0, 0, 0, 0.04)',
    '0px 4px 6px rgba(0, 0, 0, 0.04)',
    '0px 5px 7px rgba(0, 0, 0, 0.04)',
    '0px 6px 8px rgba(0, 0, 0, 0.04)',
    '0px 7px 9px rgba(0, 0, 0, 0.04)',
    '0px 8px 10px rgba(0, 0, 0, 0.04)',
    '0px 9px 11px rgba(0, 0, 0, 0.04)',
    '0px 10px 12px rgba(0, 0, 0, 0.04)',
    '0px 11px 13px rgba(0, 0, 0, 0.04)',
    '0px 12px 14px rgba(0, 0, 0, 0.04)',
    '0px 13px 15px rgba(0, 0, 0, 0.04)',
    '0px 14px 16px rgba(0, 0, 0, 0.04)',
    '0px 15px 17px rgba(0, 0, 0, 0.04)',
    '0px 16px 18px rgba(0, 0, 0, 0.04)',
    '0px 17px 19px rgba(0, 0, 0, 0.04)',
    '0px 18px 20px rgba(0, 0, 0, 0.04)',
    '0px 19px 21px rgba(0, 0, 0, 0.04)',
    '0px 20px 22px rgba(0, 0, 0, 0.04)',
    '0px 21px 23px rgba(0, 0, 0, 0.04)',
    '0px 22px 24px rgba(0, 0, 0, 0.04)',
    '0px 23px 25px rgba(0, 0, 0, 0.04)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontWeight: 600,
          textTransform: 'none',
          padding: '10px 20px',
          boxShadow: 'none',
          transition: 'all 0.2s',
          minHeight: 44, // Touch-friendly minimum height
          '@media (max-width: 600px)': {
            padding: '8px 16px',
            minHeight: 48, // Larger touch target on mobile
            fontSize: '0.875rem',
          },
          '&:focus-visible': {
            outline: '3px solid #0052CC',
            outlineOffset: '2px',
            boxShadow: '0 0 0 4px rgba(0, 102, 255, 0.25)'
          },
          '&:hover': {
            '@media (hover: hover)': { // Only on devices that support hover
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 12px rgba(0, 102, 255, 0.15)',
            },
          },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #0066FF 0%, #0052CC 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #0052CC 0%, #003D99 100%)',
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          // Ensure IconButtons are always circular
          aspectRatio: '1/1',
          borderRadius: '50%',
          transition: 'all 0.2s',
          '&:focus-visible': {
            outline: '3px solid #0052CC',
            outlineOffset: '2px',
            boxShadow: '0 0 0 4px rgba(0, 102, 255, 0.25)'
          },
          '&:hover': {
            transform: 'scale(1.05)',
          },
        },
        sizeSmall: {
          width: 32,
          height: 32,
        },
        sizeMedium: {
          width: 40,
          height: 40,
        },
        sizeLarge: {
          width: 48,
          height: 48,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
          transition: 'all 0.3s',
          '&:hover': {
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: 12,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            transition: 'all 0.2s',
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#0066FF',
              },
            },
            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#0066FF',
                borderWidth: 2,
              },
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
        filled: {
          backgroundColor: alpha('#0066FF', 0.08),
          color: '#0066FF',
          '&.MuiChip-colorSuccess': {
            backgroundColor: alpha('#10B981', 0.08),
            color: '#10B981',
          },
          '&.MuiChip-colorError': {
            backgroundColor: alpha('#EF4444', 0.08),
            color: '#EF4444',
          },
          '&.MuiChip-colorWarning': {
            backgroundColor: alpha('#F59E0B', 0.08),
            color: '#F59E0B',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        standardSuccess: {
          backgroundColor: alpha('#10B981', 0.08),
          color: '#059669',
        },
        standardError: {
          backgroundColor: alpha('#EF4444', 0.08),
          color: '#DC2626',
        },
        standardWarning: {
          backgroundColor: alpha('#F59E0B', 0.08),
          color: '#D97706',
        },
        standardInfo: {
          backgroundColor: alpha('#0EA5E9', 0.08),
          color: '#0284C7',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(0, 0, 0, 0.06)',
        },
        head: {
          fontWeight: 600,
          backgroundColor: '#F8FAFF',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
          backgroundColor: '#fff',
          color: '#0F172A',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          '&.Mui-selected': {
            backgroundColor: alpha('#0066FF', 0.08),
            color: '#0066FF',
            '&:hover': {
              backgroundColor: alpha('#0066FF', 0.12),
            },
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          '&.Mui-selected': {
            backgroundColor: '#0066FF',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#0052CC',
            },
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: alpha('#0066FF', 0.08),
        },
        bar: {
          borderRadius: 4,
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        // Global focus-visible styles for interactive elements
        'button:focus-visible, [role="button"]:focus-visible, a:focus-visible, .MuiButton-root:focus-visible, .MuiIconButton-root:focus-visible, .MuiCardActionArea-root:focus-visible': {
          outline: '3px solid #0052CC',
          outlineOffset: '2px',
          boxShadow: '0 0 0 4px rgba(0, 102, 255, 0.25)'
        },
        // Reduced motion preferences
        '@media (prefers-reduced-motion: reduce)': {
          '*': {
            animation: 'none !important',
            transition: 'none !important',
            scrollBehavior: 'auto !important',
          },
        },
      },
    },
  },
});

// Create light theme
export const lightTheme = theme;

// Dark theme with compact mode support
export const createDarkTheme = (compactMode = false) => createAppTheme('dark', compactMode);

const darkTheme = createTheme({
  ...baseThemeConfig,
  palette: darkPalette,
  typography: theme.typography,
  shadows: theme.shadows,
  components: {
    ...theme.components,
    // Dark mode specific component overrides
    MuiIconButton: {
      styleOverrides: {
        root: {
          // Ensure IconButtons are always circular in dark mode too
          aspectRatio: '1/1',
          borderRadius: '50%',
          transition: 'all 0.2s',
          '&:hover': {
            transform: 'scale(1.05)',
          },
        },
        sizeSmall: {
          width: 32,
          height: 32,
        },
        sizeMedium: {
          width: 40,
          height: 40,
        },
        sizeLarge: {
          width: 48,
          height: 48,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
          transition: 'all 0.3s',
          backgroundImage: 'none',
          '&:hover': {
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.16)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: 12,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            transition: 'all 0.2s',
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: darkPalette.primary.main,
              },
            },
            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: darkPalette.primary.main,
                borderWidth: 2,
              },
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: darkPalette.divider,
        },
        head: {
          fontWeight: 600,
          backgroundColor: alpha(darkPalette.background.paper, 0.8),
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
          backgroundColor: darkPalette.background.paper,
          color: darkPalette.text.primary,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: `1px solid ${darkPalette.divider}`,
          backgroundColor: darkPalette.background.paper,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          '&.Mui-selected': {
            backgroundColor: alpha(darkPalette.primary.main, 0.16),
            color: darkPalette.primary.main,
            '&:hover': {
              backgroundColor: alpha(darkPalette.primary.main, 0.24),
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
        filled: {
          backgroundColor: alpha(darkPalette.primary.main, 0.16),
          color: darkPalette.primary.main,
          '&.MuiChip-colorSuccess': {
            backgroundColor: alpha(darkPalette.success.main, 0.16),
            color: darkPalette.success.main,
          },
          '&.MuiChip-colorError': {
            backgroundColor: alpha(darkPalette.error.main, 0.16),
            color: darkPalette.error.main,
          },
          '&.MuiChip-colorWarning': {
            backgroundColor: alpha(darkPalette.warning.main, 0.16),
            color: darkPalette.warning.main,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        standardSuccess: {
          backgroundColor: alpha(darkPalette.success.main, 0.16),
          color: darkPalette.success.light,
        },
        standardError: {
          backgroundColor: alpha(darkPalette.error.main, 0.16),
          color: darkPalette.error.light,
        },
        standardWarning: {
          backgroundColor: alpha(darkPalette.warning.main, 0.16),
          color: darkPalette.warning.light,
        },
        standardInfo: {
          backgroundColor: alpha(darkPalette.info.main, 0.16),
          color: darkPalette.info.light,
        },
      },
    },
  },
});


// Export default theme
export default theme;
